import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2, MailCheck } from 'lucide-react';
import { LeadPhoneField } from '@/components/lead-quality/LeadPhoneField';
import { useLeadQualityEngine } from '@/components/lead-quality/useLeadQualityEngine';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { trackFunnelEvent } from '@/services/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DESTINATIONS = ['Germany', 'United Kingdom', 'Canada', 'Australia', 'Ireland', 'New Zealand', 'Dubai (UAE)', 'USA'];
const INTAKES = ['Fall 2026', 'Spring 2027', 'Summer 2027', 'Not Sure Yet'];
const EDUCATION_LEVELS = ['10th', '12th', 'Diploma', 'Bachelor', 'Master', 'PhD'];
const ENGLISH_TESTS = ['Not Attempted Yet', 'Planning to Take', 'IELTS', 'PTE', 'TOEFL', 'Duolingo', 'Other'];

const detailToText = (detail) => {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || '').filter(Boolean).join(' ');
  return 'Something went wrong. Please try again.';
};

const Select = ({ label, value, onChange, options, testId }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>
    <select
      required
      className="sa-input w-full"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      data-testid={testId}
    >
      <option value="">Select…</option>
      {options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
    </select>
  </div>
);

// Shared Secure Lead Capture form (Feature: ENABLE_SECURE_LEAD_PIPELINE).
// Email OTP verification + WhatsApp validation (LQE) + verified-only CRM routing.
export const SecureLeadForm = ({ source = 'secure-lead-pipeline' }) => {
  const navigate = useNavigate();
  const lqe = useLeadQualityEngine();
  const { flags } = useFeatureFlags();
  const [form, setForm] = useState({ name: '', email: '', destination: '', intake: '', education: '', englishTest: '' });
  const [emailState, setEmailState] = useState('idle'); // idle|sending|sent|verifying|verified
  const [otp, setOtp] = useState('');
  const [otpMsg, setOtpMsg] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const debugOtpRef = useRef('');

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const setField = (key) => (value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (key === 'email' && emailState !== 'idle') {
      setEmailState('idle');
      setVerificationToken('');
      setOtp('');
      setOtpMsg('');
    }
  };

  const sendOtp = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setOtpMsg('Please enter a valid email address first.');
      return;
    }
    setEmailState('sending');
    setOtpMsg('');
    try {
      const res = await fetch(`${API_URL}/api/secure-leads/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, website: lqe.honeypot }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setEmailState('idle');
        setOtpMsg(detailToText(data.detail));
        trackFunnelEvent('email_otp_failed', { reason: res.status === 429 ? 'rate_limited' : 'pre_validation' });
        return;
      }
      if (data.debug_otp) debugOtpRef.current = data.debug_otp;
      setEmailState('sent');
      setCooldown(flags.otp_resend_cooloff_sec || 30);
      setOtpMsg(data.channel === 'dev'
        ? 'Dev mode: verification code auto-filled below (email delivery activates once configured).'
        : 'We\'ve emailed you a 6-digit code. It expires in 10 minutes.');
      if (data.debug_otp) setOtp(data.debug_otp);
      trackFunnelEvent('email_otp_sent', { channel: data.channel || 'email' });
    } catch {
      setEmailState('idle');
      setOtpMsg('Network issue — please check your connection and try again.');
      trackFunnelEvent('email_otp_failed', { reason: 'network' });
    }
  };

  const verifyOtp = async () => {
    setEmailState('verifying');
    setOtpMsg('');
    try {
      const res = await fetch(`${API_URL}/api/secure-leads/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, otp }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setEmailState('sent');
        setOtpMsg(detailToText(data.detail));
        trackFunnelEvent('email_otp_failed', { reason: res.status === 423 ? 'locked' : res.status === 410 ? 'expired' : 'invalid' });
        return;
      }
      setVerificationToken(data.verification_token);
      setEmailState('verified');
      setOtpMsg('');
      trackFunnelEvent('email_otp_verified', {});
    } catch {
      setEmailState('sent');
      setOtpMsg('Network issue — please try again.');
      trackFunnelEvent('email_otp_failed', { reason: 'network' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (emailState !== 'verified') {
      setFormError('Please verify your email with the OTP before submitting.');
      return;
    }
    const guard = await lqe.guardSubmit();
    if (!guard.ok) return;
    if (submitting) return; // idempotency — double-click guard
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/secure-leads/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: lqe.phone,
          destination: form.destination,
          intake: form.intake,
          education: form.education,
          english_test: form.englishTest,
          verification_token: verificationToken,
          source,
          source_page_url: typeof window !== 'undefined' ? window.location.href : '',
          phone_validation_status: guard.payloadMeta.phone_validation_status,
          website: guard.payloadMeta.website,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (lqe.interpretResponse(res)) return;
        if (res.status === 401) {
          setEmailState('idle');
          setVerificationToken('');
          setFormError('Your email verification expired — please verify again.');
        } else {
          setFormError(detailToText(data.detail));
        }
        return;
      }
      lqe.markSubmitSuccess();
      trackFunnelEvent('form_submitted', {
        destination: form.destination,
        education: form.education,
        verification_result: 'email_verified',
      });
      navigate('/study-abroad/thank-you', { state: { fromSubmission: true } });
    } catch {
      setFormError('Network issue — your details were not submitted. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 text-left" data-testid="secure-lead-form" aria-label="Study abroad enquiry form">
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Full Name *</label>
        <input
          type="text" required className="sa-input w-full" value={form.name}
          onChange={(e) => setField('name')(e.target.value)}
          aria-label="Full Name" data-testid="slf-name"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Email ID * <span className="font-normal text-slate-400">(OTP verification)</span></label>
        <div className="flex gap-2 items-start">
          <input
            type="email" required className="sa-input w-full" value={form.email}
            onChange={(e) => setField('email')(e.target.value)}
            disabled={emailState === 'verified'}
            aria-label="Email ID" data-testid="slf-email"
          />
          {emailState === 'verified' ? (
            <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold whitespace-nowrap mt-3" data-testid="slf-email-verified-badge">
              <CheckCircle className="w-4 h-4" /> Verified
            </span>
          ) : (
            <button
              type="button"
              onClick={sendOtp}
              disabled={emailState === 'sending' || cooldown > 0}
              className="shrink-0 px-4 py-3 rounded-[10px] bg-[#003087] text-white text-xs font-semibold disabled:opacity-50 transition-opacity"
              data-testid="slf-send-otp-btn"
            >
              {emailState === 'sending' ? <Loader2 className="w-4 h-4 animate-spin" /> : cooldown > 0 ? `Resend in ${cooldown}s` : emailState === 'sent' || emailState === 'verifying' ? 'Resend Code' : 'Verify Email'}
            </button>
          )}
        </div>
        {(emailState === 'sent' || emailState === 'verifying') && (
          <div className="flex gap-2 mt-2 items-center">
            <MailCheck className="w-4 h-4 text-slate-400 shrink-0" aria-hidden="true" />
            <input
              type="text" inputMode="numeric" maxLength={6} placeholder="Enter 6-digit code"
              className="sa-input w-full tracking-[0.3em]" value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              aria-label="One-time verification code" data-testid="slf-otp-input"
            />
            <button
              type="button" onClick={verifyOtp}
              disabled={otp.length !== 6 || emailState === 'verifying'}
              className="shrink-0 px-4 py-3 rounded-[10px] bg-emerald-600 text-white text-xs font-semibold disabled:opacity-50"
              data-testid="slf-verify-otp-btn"
            >
              {emailState === 'verifying' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
            </button>
          </div>
        )}
        {otpMsg && <p className="text-xs mt-1.5 text-slate-500" role="status" data-testid="slf-otp-msg">{otpMsg}</p>}
      </div>

      <LeadPhoneField engine={lqe} testId="slf-phone" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
        <Select label="Preferred Destination *" value={form.destination} onChange={setField('destination')} options={DESTINATIONS} testId="slf-destination" />
        <Select label="Preferred Intake *" value={form.intake} onChange={setField('intake')} options={INTAKES} testId="slf-intake" />
        <Select label="Highest Education *" value={form.education} onChange={setField('education')} options={EDUCATION_LEVELS} testId="slf-education" />
        <Select label="IELTS / English Test *" value={form.englishTest} onChange={setField('englishTest')} options={ENGLISH_TESTS} testId="slf-english-test" />
      </div>

      {formError && <p className="text-xs text-red-600 font-medium" role="alert" data-testid="slf-form-error">{formError}</p>}

      <button
        type="submit"
        disabled={submitting}
        className="w-full py-3.5 rounded-[10px] bg-[#003087] hover:bg-[#00246a] text-white font-semibold text-sm transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
        data-testid="slf-submit-btn"
      >
        {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Submitting…</>) : 'Get My Free Study Abroad Guide'}
      </button>
      <p className="text-[10px] text-slate-400 text-center">Verified enquiries get priority counsellor callbacks + the free Study Abroad Guide 2026 PDF.</p>
    </form>
  );
};
